import { PerspectiveType } from "@xmiles/holopix-ai";
import {
  FREE_PERSPECTIVE_DEFAULT_CAMERA,
  FREE_PERSPECTIVE_LEGACY_SOURCE_ALIASES,
  FREE_PERSPECTIVE_LEGACY_VIEW_COUNT_ALIASES,
  FREE_PERSPECTIVE_PRESET_OPTIONS,
  FREE_PERSPECTIVE_SIZE_OPTIONS,
} from "./constants";
import type {
  FreePerspectiveCameraParams,
  FreePerspectiveEntrySource,
  FreePerspectiveGenerateParams,
  FreePerspectiveLegacyMeta,
  FreePerspectivePresetOption,
  FreePerspectiveSizeOption,
  FreePerspectiveSizeValue,
  FreePerspectiveViewAngleSource,
} from "./types";

export function cloneFreePerspectiveCameraParams(cameraParams?: Partial<FreePerspectiveCameraParams> | null): FreePerspectiveCameraParams {
  return {
    horizontalAngle: Number(cameraParams?.horizontalAngle ?? FREE_PERSPECTIVE_DEFAULT_CAMERA.horizontalAngle),
    verticalAngle: Number(cameraParams?.verticalAngle ?? FREE_PERSPECTIVE_DEFAULT_CAMERA.verticalAngle),
    distance: Number(cameraParams?.distance ?? FREE_PERSPECTIVE_DEFAULT_CAMERA.distance),
  };
}

export function resolveFreePerspectivePresetOption(viewAngleId?: PerspectiveType | null): FreePerspectivePresetOption {
  return (
    FREE_PERSPECTIVE_PRESET_OPTIONS.find((option) => option.id === viewAngleId) ?? FREE_PERSPECTIVE_PRESET_OPTIONS[0]
  );
}

export function resolveFreePerspectivePresetByLegacyMeta(legacyMeta?: FreePerspectiveLegacyMeta | null): FreePerspectivePresetOption {
  const legacyKey = normalizeLegacyKey(legacyMeta?.viewCountType);
  if (legacyKey && legacyKey in FREE_PERSPECTIVE_LEGACY_VIEW_COUNT_ALIASES) {
    const matchedId = FREE_PERSPECTIVE_LEGACY_VIEW_COUNT_ALIASES[legacyKey];
    const matchedPreset = FREE_PERSPECTIVE_PRESET_OPTIONS.find((option) => option.id === matchedId);
    if (matchedPreset) {
      return matchedPreset;
    }
  }

  const legacyViewAngleId = legacyMeta?.legacyViewAngleId;
  if (typeof legacyViewAngleId === "number") {
    const matchedPreset = FREE_PERSPECTIVE_PRESET_OPTIONS.find((option) => option.id === legacyViewAngleId);
    if (matchedPreset) {
      return matchedPreset;
    }
  }

  return FREE_PERSPECTIVE_PRESET_OPTIONS[0];
}

export function resolveFreePerspectiveSizeOption(value?: string | null): FreePerspectiveSizeOption {
  return FREE_PERSPECTIVE_SIZE_OPTIONS.find((option) => option.value === value) ?? FREE_PERSPECTIVE_SIZE_OPTIONS[0];
}

export function resolveFreePerspectiveViewAngleSource(source?: string | null): FreePerspectiveViewAngleSource {
  const normalizedSource = normalizeLegacySource(source);
  if (normalizedSource && normalizedSource in FREE_PERSPECTIVE_LEGACY_SOURCE_ALIASES) {
    return FREE_PERSPECTIVE_LEGACY_SOURCE_ALIASES[normalizedSource] as FreePerspectiveViewAngleSource;
  }
  return "preset_click";
}

export function buildFreePerspectiveGenerateParams(params: {
  module: number;
  originImgUrl: string;
  size: FreePerspectiveSizeValue;
  viewAngleId?: PerspectiveType | null;
  cameraParams?: Partial<FreePerspectiveCameraParams> | null;
  viewAngleSource?: FreePerspectiveViewAngleSource | null;
  legacyPerspectiveMeta?: FreePerspectiveLegacyMeta | null;
  prompt?: string;
  freeCanvasSource?: FreePerspectiveEntrySource;
}): FreePerspectiveGenerateParams {
  const preset = resolveFreePerspectivePresetOption(params.viewAngleId ?? null);
  const viewAngleId = params.viewAngleId ?? preset.id;
  const viewAngleSource = params.viewAngleSource ?? resolveFreePerspectiveViewAngleSource(params.legacyPerspectiveMeta?.legacySource ?? null);

  return {
    module: params.module,
    originImgUrl: params.originImgUrl,
    prompt: params.prompt || "",
    cameraParams: cloneFreePerspectiveCameraParams(params.cameraParams ?? params.legacyPerspectiveMeta?.legacyCameraParams ?? preset.cameraParams),
    aspectRatios: params.size,
    qwenFreePerspectiveParams: {
      viewAngleId,
      viewAngleSource,
      freeCanvasSource: params.freeCanvasSource ?? "tool_panel",
    },
    legacyPerspectiveMeta: params.legacyPerspectiveMeta ?? null,
  };
}

function normalizeLegacyKey(value?: string | number | null): string {
  if (value == null) {
    return "";
  }
  return String(value).trim().toLowerCase();
}

function normalizeLegacySource(value?: string | null): string {
  if (!value) {
    return "";
  }
  return value.trim().toLowerCase();
}
